// ─────────────────────────────────────────────────────────────────────────────
// La langue de la personne (8 octobre 2026).
//
// Elle se choisit dans la barre latérale, et se garde SUR LE COMPTE
// (`user_metadata.langue` de l'identité) : on la retrouve sur un autre poste.
// Le navigateur sert de premier choix, faute de mieux.
//
// CHAQUE personne choisit sa langue, quelle que soit son organisation
// (décision du 8 octobre 2026). La clé `langue-anglaise` qui la réservait aux
// organisations qui l'avaient reçue a été retirée le jour même. Les langues
// possibles sont celles de `LANGUES` (src/i18n/index.ts) : une langue ajoutée
// là apparaît ici sans autre changement.
// ─────────────────────────────────────────────────────────────────────────────
import { computed, ref, watch } from 'vue'
import { LANGUE_SOURCE, LANGUES, estLangue, i18n, type Langue } from '@/i18n'
import { supabase, useSession } from './session'

const CLE_STOCKAGE = 'orion-langue'

function lireStockage(): Langue | null {
  try {
    const v = localStorage.getItem(CLE_STOCKAGE)
    return estLangue(v) ? v : null
  } catch {
    return null
  }
}

/** Le choix fait sur CE poste, avant que le compte ne l'ait relu. */
const choisie = ref<Langue | null>(lireStockage())

/** La première langue connue parmi celles que préfère le navigateur. */
export function langueDuNavigateur(): Langue {
  if (typeof navigator === 'undefined') return LANGUE_SOURCE
  const preferees = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const p of preferees) {
    const code = (p ?? '').toLowerCase().split('-')[0]
    const trouvee = LANGUES.find((l) => l === code)
    if (trouvee !== undefined) return trouvee
  }
  return LANGUE_SOURCE
}

export function useLangue() {
  const { user } = useSession()

  const langue = computed<Langue>(() => {
    const duCompte = (user.value?.user_metadata as { langue?: unknown } | undefined)?.langue
    return choisie.value ?? (estLangue(duCompte) ? duCompte : null) ?? langueDuNavigateur()
  })

  async function changerLangue(l: Langue): Promise<void> {
    choisie.value = l
    try { localStorage.setItem(CLE_STOCKAGE, l) } catch { /* navigation privée : le choix vaut pour la session */ }
    // Gardée sur le compte, sans attendre : un échec ne doit pas bloquer l'écran.
    void supabase.auth.updateUser({ data: { langue: l } }).catch(() => {})
  }

  return { langue, changerLangue }
}

/**
 * Aligne vue-i18n et `<html lang>` sur la langue effective. Appelée une fois,
 * par la racine de l'application.
 */
export function installerLangue(): void {
  const { langue } = useLangue()
  watch(langue, (l) => {
    i18n.global.locale.value = l
    if (typeof document !== 'undefined') document.documentElement.lang = l
  }, { immediate: true })
}
